import { vec3 } from 'gl-matrix';
import { DcmJsDatasetWrapper } from './dcmJsWrapper';
import { generateRandomRgbColor } from '~/utils/color';
import { DicomColor } from './dicomColor';
import { DicomRoi } from './dicomRoi';
import {
    ContourGeometricType,
    DicomContour,
    DicomStructureSet,
} from './dicomstructureset';
import { DicomDataset } from './engine/dataset';
import { DicomFile } from './engine/reader';
import * as tags from './engine/tags';
import { StorageSOPClassUID } from './modality';

/**
 * Builds a {@link DicomStructureSet} from the typed dataset model.
 *
 * The equivalent that reads dcmjs's keyword-addressed shape normalizes as it
 * goes — reassigning sequences to plain arrays, splitting ContourData when it
 * arrives as a string, discarding items that parsed as undefined. All of that
 * exists because the shape it reads varies with the file. Here a sequence is
 * an array and a multi-valued number is a number array, so the reader only
 * reads.
 *
 * The contour coordinates are the reason this matters beyond tidiness: a
 * structure set is mostly ContourData, projecting one parses every coordinate
 * out of its decimal string, and displaying a structure list needs none of it.
 */

/** Longest SeriesInstanceUID a DICOM UI value may carry. */
const UID_MAX_LENGTH = 64;

/** Contour types the application can draw. */
const SUPPORTED_CONTOUR_TYPES = new Set(['CLOSED_PLANAR', 'POINT']);

/**
 * Collects every image instance the structure set references, in the order
 * encountered, without repeating one.
 */
function collectReferencedSopUids(dataset: DicomDataset, into: string[]): void {
    for (const frameOfReference of dataset.sequence(
        tags.REFERENCED_FRAME_OF_REFERENCE_SEQUENCE
    )) {
        for (const study of frameOfReference.sequence(
            tags.RT_REFERENCED_STUDY_SEQUENCE
        )) {
            for (const series of study.sequence(
                tags.RT_REFERENCED_SERIES_SEQUENCE
            )) {
                for (const image of series.sequence(
                    tags.CONTOUR_IMAGE_SEQUENCE
                )) {
                    const uid = image.string(tags.REFERENCED_SOP_INSTANCE_UID);
                    if (uid && !into.includes(uid)) {
                        into.push(uid);
                    }
                }
            }
        }
    }
}

/** The contours of one ROI, and the image instances they reference. */
function readContours(
    roiContour: DicomDataset,
    referencedSopUids: string[]
): DicomContour[] {
    const contours: DicomContour[] = [];

    for (const contour of roiContour.sequence(tags.CONTOUR_SEQUENCE)) {
        const geometricType = contour.string(tags.CONTOUR_GEOMETRIC_TYPE);
        if (!geometricType || !SUPPORTED_CONTOUR_TYPES.has(geometricType)) {
            console.error(`Unsupported contour type ${geometricType}`);
            continue;
        }

        const [referencedImage] = contour.sequence(tags.CONTOUR_IMAGE_SEQUENCE);
        const referencedSopInstanceUid = referencedImage?.string(
            tags.REFERENCED_SOP_INSTANCE_UID
        );
        if (
            referencedSopInstanceUid &&
            !referencedSopUids.includes(referencedSopInstanceUid)
        ) {
            referencedSopUids.push(referencedSopInstanceUid);
        }

        const declaredPoints = contour.number(tags.NUMBER_OF_CONTOUR_POINTS);
        const coordinates = contour.numbers(tags.CONTOUR_DATA);
        if (
            declaredPoints === undefined ||
            declaredPoints * 3 !== coordinates.length
        ) {
            throw new Error(
                `Unexpected number of contour points ${declaredPoints} while ` +
                    `ContourData length is ${coordinates.length}`
            );
        }

        const points: vec3[] = [];
        for (let i = 0; i < declaredPoints; i++) {
            points.push(
                vec3.fromValues(
                    coordinates[i * 3],
                    coordinates[i * 3 + 1],
                    coordinates[i * 3 + 2]
                )
            );
        }
        if (points.length === 0) {
            continue;
        }

        contours.push(
            new DicomContour(
                geometricType as ContourGeometricType,
                points,
                referencedImage?.string(tags.REFERENCED_SOP_CLASS_UID),
                referencedSopInstanceUid
            )
        );
    }

    return contours;
}

/** The ROI display colour, or a generated one when the file carries none. */
function readColor(roiContour: DicomDataset): DicomColor {
    const values = roiContour.numbers(tags.ROI_DISPLAY_COLOR);
    if (values.length < 3) {
        const generated = generateRandomRgbColor();
        return { R: generated[0], G: generated[1], B: generated[2] };
    }
    return { R: values[0], G: values[1], B: values[2] };
}

function readRois(
    dataset: DicomDataset,
    wrapper: DcmJsDatasetWrapper,
    referencedSopUids: string[]
): Map<number, DicomRoi> {
    // The contour items are indexed first so an ROI's colour is known before
    // it is constructed. Assigning it afterwards would go through the setter,
    // which writes the colour back into the dataset — a write during a read,
    // which is what stops an unedited structure set from saving as it arrived.
    const contourItems = new Map<number, DicomDataset>();
    for (const roiContour of dataset.sequence(tags.ROI_CONTOUR_SEQUENCE)) {
        const referenced = roiContour.number(tags.REFERENCED_ROI_NUMBER);
        if (referenced !== undefined) {
            contourItems.set(referenced, roiContour);
        }
    }

    const rois = new Map<number, DicomRoi>();
    for (const item of dataset.sequence(tags.STRUCTURE_SET_ROI_SEQUENCE)) {
        const roiNumber = item.number(tags.ROI_NUMBER);
        if (roiNumber === undefined) {
            continue;
        }
        const roiContour = contourItems.get(roiNumber);
        const roi = new DicomRoi(
            wrapper,
            roiNumber,
            item.string(tags.ROI_NAME) ?? '',
            roiContour ? readColor(roiContour) : undefined
        );
        if (roiContour) {
            // Only sets the field; unlike colour, this setter does not write.
            roi.contours = readContours(roiContour, referencedSopUids);
        }
        rois.set(roiNumber, roi);
    }

    for (const observation of dataset.sequence(
        tags.RT_ROI_OBSERVATIONS_SEQUENCE
    )) {
        const referenced = observation.number(tags.REFERENCED_ROI_NUMBER);
        if (referenced !== undefined && !rois.has(referenced)) {
            console.warn(
                `StructureSet ROIObservationSequence contains roiNumber ` +
                    `${referenced}, but that ROINumber is not included in the ` +
                    `structureSetROISequence`
            );
        }
    }

    return rois;
}

/**
 * Reads a structure set, or returns null when the object does not reference an
 * image series the application can attach it to.
 */
export function readStructureSet(file: DicomFile): DicomStructureSet | null {
    const dataset = file.dataset;

    const [frameOfReference] = dataset.sequence(
        tags.REFERENCED_FRAME_OF_REFERENCE_SEQUENCE
    );
    const [study] =
        frameOfReference?.sequence(tags.RT_REFERENCED_STUDY_SEQUENCE) ?? [];
    const [series] = study?.sequence(tags.RT_REFERENCED_SERIES_SEQUENCE) ?? [];
    const referencedSeriesUid = series?.string(tags.SERIES_INSTANCE_UID);
    if (!referencedSeriesUid) {
        return null;
    }

    const [firstImage] = series.sequence(tags.CONTOUR_IMAGE_SEQUENCE);
    const referencedSopClassUid =
        firstImage?.string(tags.REFERENCED_SOP_CLASS_UID) ??
        StorageSOPClassUID.CTImageStorage;

    const wrapper = new DcmJsDatasetWrapper(file);
    const referencedSopUids: string[] = [];
    const rois = readRois(dataset, wrapper, referencedSopUids);
    collectReferencedSopUids(dataset, referencedSopUids);

    return new DicomStructureSet(
        wrapper,
        dataset.string(tags.SOP_INSTANCE_UID) as string,
        frameOfReference?.string(tags.FRAME_OF_REFERENCE_UID) as string,
        referencedSeriesUid.substring(0, UID_MAX_LENGTH),
        rois,
        referencedSopUids,
        referencedSopClassUid,
        dataset.string(tags.SERIES_INSTANCE_UID) as string
    );
}
